import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Palette, Eye, Building, Crown, ArrowLeft, Users } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/DashboardSelector.css';

const DashboardSelector = () => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [galleryAccess, setGalleryAccess] = useState([]);
  const [organizationMemberships, setOrganizationMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check auth and fetch user data
  useEffect(() => {
    const initializePage = async () => {
      try {
        setLoading(true);

        // Check if user is logged in
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

        if (authError || !authUser) {
          console.log('DashboardSelector: No user found, redirecting to login');
          navigate('/login');
          return;
        }

        setUser(authUser);
        console.log('DashboardSelector: User authenticated:', authUser.email);

        // Strategy: For admin site, redirect authenticated users directly to curator dashboard
        // This can be expanded later to check roles if needed
        console.log('DashboardSelector: Redirecting to curator dashboard');
        navigate('/curator/dashboard');
        return;

        // Fetch user roles, gallery access, and organization memberships in parallel
        const [rolesResponse, galleryResponse, orgResponse] = await Promise.all([
          supabase
            .from('user_roles')
            .select('role_id')
            .eq('user_id', authUser.id),
          supabase
            .from('gallery_access')
            .select('*')
            .eq('user_uuid', authUser.id)
            .eq('is_active', true),
          supabase
            .from('organization_members')
            .select(`
              *,
              organizations:organization_id (
                organization_id,
                name,
                slug,
                logo
              )
            `)
            .eq('user_uuid', authUser.id)
            .eq('is_active', true)
        ]);

        if (rolesResponse.error) {
          console.error('Error fetching user roles:', rolesResponse.error);
          setUserRoles([]);
        } else {
          setUserRoles(rolesResponse.data || []);
          console.log('DashboardSelector: User roles:', rolesResponse.data);
        }

        if (galleryResponse.error) {
          console.error('Error fetching gallery access:', galleryResponse.error);
          setGalleryAccess([]);
        } else {
          setGalleryAccess(galleryResponse.data || []);
          console.log('DashboardSelector: Gallery access:', galleryResponse.data);
        }

        if (orgResponse.error) {
          console.error('Error fetching organization memberships:', orgResponse.error);
          setOrganizationMemberships([]);
        } else {
          setOrganizationMemberships(orgResponse.data || []);
          console.log('DashboardSelector: Organization memberships:', orgResponse.data);
        }
      } catch (error) {
        console.error('Error initializing dashboard selector:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [navigate]);

  // Define all possible dashboards
  const allDashboards = [
    {
      title: 'Artist Studio',
      description: 'Manage your artworks and portfolio',
      href: '/artist/dashboard',
      icon: Palette,
      type: 'artist',
      requiredRole: [1, 4], // Artist roles
      requiredAccess: null
    },
    {
      title: 'Collector Hub',
      description: 'Discover and collect art',
      href: '/collector/dashboard',
      icon: Eye,
      type: 'collector',
      requiredRole: [2], // Collector role
      requiredAccess: null
    },
    {
      title: 'Gallery Admin',
      description: 'Manage gallery operations',
      href: '/gallery/dashboard',
      icon: Building,
      type: 'gallery',
      requiredRole: null,
      requiredAccess: 'gallery' // Requires gallery access
    },
    {
      title: 'Curator Dashboard',
      description: 'Manage collections, submissions, and events',
      href: '/curator/dashboard',
      icon: Users,
      type: 'curator',
      requiredRole: null,
      requiredAccess: 'organization' // Requires organization membership OR gallery access
    },
    {
      title: 'Admin Dashboard',
      description: 'Full platform administration',
      href: '/admin/dashboard',
      icon: Crown,
      type: 'admin',
      requiredRole: [3], // Company admin role
      requiredAccess: null
    }
  ];

  // Get all dashboards and mark accessibility
  const getAllDashboardsWithAccess = () => {
    if (!user) return [];
    
    return allDashboards.map(dashboard => {
      let hasAccess = false;
      let reason = '';
      let accessContext = null; // Store org/gallery info for context
      
      // Company admins (role_id = 3) have access to everything
      if (userRoles.some(role => role.role_id === 3)) {
        hasAccess = true;
        accessContext = { type: 'admin', label: 'VibrantCast Admin' };
      } else {
        // Check role requirements
        if (dashboard.requiredRole && !userRoles.some(role => dashboard.requiredRole.includes(role.role_id))) {
          hasAccess = false;
          reason = 'Role required';
        } else if (dashboard.requiredAccess === 'gallery' && galleryAccess.length === 0) {
          hasAccess = false;
          reason = 'Gallery access required';
        } else if (dashboard.requiredAccess === 'organization') {
          // Curator Dashboard: requires organization membership OR gallery access
          if (organizationMemberships.length > 0) {
            hasAccess = true;
            accessContext = {
              type: 'organization',
              organizations: organizationMemberships.map(m => ({
                id: m.organization_id,
                name: m.organizations?.name,
                role: m.role
              }))
            };
          } else if (galleryAccess.length > 0) {
            hasAccess = true;
            accessContext = { type: 'gallery', label: 'Gallery Access' };
          } else {
            hasAccess = false;
            reason = 'Organization or gallery access required';
          }
        } else {
          hasAccess = true;
        }
      }
      
      // If we have no role data, be more permissive to prevent stuck state
      if (userRoles.length === 0 && organizationMemberships.length === 0 && galleryAccess.length === 0 && !hasAccess) {
        hasAccess = true;
        reason = 'Access granted (fallback mode)';
      }
      
      return {
        ...dashboard,
        hasAccess,
        reason,
        accessContext
      };
    });
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const dashboards = getAllDashboardsWithAccess();

  if (loading) {
    return (
      <div className="dashboard-selector">
        <div className="dashboard-selector-container">
          <div className="dashboard-selector-header">
            <h1 className="dashboard-selector-title">Loading...</h1>
            <p className="dashboard-selector-subtitle">Checking your permissions...</p>
            <div className="loading-debug">
              <p>User: {user ? 'Found' : 'Checking...'}</p>
              <p>Roles: {userRoles.length > 0 ? `${userRoles.length} found` : 'Loading...'}</p>
              <p>Gallery Access: {galleryAccess.length > 0 ? `${galleryAccess.length} found` : 'Loading...'}</p>
              <p>Organizations: {organizationMemberships.length > 0 ? `${organizationMemberships.length} found` : 'Loading...'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-selector">
      <div className="dashboard-selector-container">
        <div className="dashboard-selector-header">
          <Link to="/" className="dashboard-back-link">
            <ArrowLeft />
            <span>Back to Home</span>
          </Link>
          <h1 className="dashboard-selector-title">
            Choose Your Workspace
          </h1>
          <p className="dashboard-selector-subtitle">
            Welcome back! Select the dashboard you'd like to use today
          </p>
          {user && (
            <div className="user-info">
              <span className="user-email">{user.email}</span>
              {userRoles.length > 0 && (
                <span className="user-roles">
                  Roles: {userRoles.map(role => {
                    const roleNames = { 1: 'Artist', 2: 'Collector', 3: 'Company Admin', 4: 'Artist' };
                    return roleNames[role.role_id] || 'Unknown';
                  }).join(', ')}
                </span>
              )}
              {organizationMemberships.length > 0 && (
                <span className="user-orgs">
                  Organizations: {organizationMemberships.map(m => 
                    `${m.organizations?.name || 'Unknown'} (${m.role})`
                  ).join(', ')}
                </span>
              )}
              <button 
                onClick={handleSignOut}
                className="logout-button"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
        
        <div className="dashboard-selector-grid">
          {dashboards.map((dashboard) => (
            <div
              key={dashboard.title}
              className={`dashboard-card ${dashboard.type} ${!dashboard.hasAccess ? 'disabled' : ''}`}
            >
              <div className="dashboard-card-content">
                <div className="dashboard-card-icon">
                  <dashboard.icon />
                </div>
                <h3 className="dashboard-card-title">
                  {dashboard.title}
                </h3>
                <p className="dashboard-card-description">
                  {dashboard.description}
                </p>
                {dashboard.hasAccess ? (
                  <Link to={dashboard.href} className="dashboard-card-button">
                    Enter {dashboard.title}
                  </Link>
                ) : (
                  <div className="dashboard-card-disabled">
                    <span className="disabled-text">{dashboard.reason}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardSelector;
